import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { appointmentsAPI } from "@/lib/appointments-api";
import { queryKeys } from "@/lib/query-keys";
import type { ReceptionNotificationChannel } from "@/types/appointments";

const newIdempotencyKey = (): string => crypto.randomUUID();

export const useReceptionCollection = (appointmentId: string | null) => {
  const queryClient = useQueryClient();
  const queryKey = appointmentId
    ? queryKeys.appointments.collection(appointmentId)
    : queryKeys.appointments.all;
  const snapshot = useQuery({
    queryKey,
    queryFn: () => appointmentsAPI.getCollectionSnapshot(appointmentId!),
    enabled: Boolean(appointmentId),
    refetchInterval: (query) => {
      return query.state.data?.next_poll_after_ms ?? false;
    },
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });

  const update = (data: unknown) => {
    queryClient.setQueryData(queryKey, data);
    void queryClient.invalidateQueries({ queryKey });
  };

  const notify = useMutation({
    mutationFn: (args: {
      channel: ReceptionNotificationChannel;
      email?: string;
    }) =>
      appointmentsAPI.sendCollectionNotification(
        appointmentId!,
        snapshot.data?.attempt?.id ?? "",
        args.channel,
        args.email,
        newIdempotencyKey()
      ),
    onSuccess: update,
  });
  const refresh = useMutation({
    mutationFn: () =>
      appointmentsAPI.refreshCollection(appointmentId!, newIdempotencyKey()),
    onSuccess: update,
  });
  const cancel = useMutation({
    mutationFn: () =>
      appointmentsAPI.cancelCollection(appointmentId!, newIdempotencyKey()),
    onSuccess: update,
  });

  return { snapshot, notify, refresh, cancel };
};
